import { EmploymentSalaryType, Gender } from '@sysarb/sysarb-types-v2/general/enums';
import { CareerType } from '@sysarb/sysarb-types-v2/services/v1/job-architecture';
import type { EmploymentDashboardData, PortalEmploymentStatistics } from '@sysarb/sysarb-types-v2/services/v1/employment/involvePortal';
import { hiddenBySettings, visibleSection } from '@sysarb/sysarb-types-v2/services/v1/employment/involvePortal';
import { ref } from 'vue';

export type MockInvolveScenario =
    | 'all-visible'         // All sections visible, no suppressions
    | 'all-suppressed'      // All sections visible, all Suppressible fields gdpr-suppressed
    | 'mixed'               // All sections visible, mix of suppressed and non-suppressed fields
    | 'total-hidden'        // All sections visible, total-salary halves switched off in settings
    | 'job-profile-empty';  // Job profile subsections switched on but carrying no data

/** Switch the active mock scenario. Reactive — changing this triggers query re-evaluation. */
export const currentInvolveScenario = ref<MockInvolveScenario>('all-visible');

export function getMockInvolveDashboard(): EmploymentDashboardData {
    const scenario = currentInvolveScenario.value;

    if (scenario === 'all-suppressed') return buildAllSuppressed();
    if (scenario === 'mixed') return buildMixed();
    if (scenario === 'total-hidden') return buildTotalHidden();
    if (scenario === 'job-profile-empty') return buildJobProfileEmpty();
    return buildAllVisible();
}

// ─── Scenarios ────────────────────────────────────────────────────────────────
const hiddenBySetting = { suppressed: true, reason: 'hidden-by-settings' } as const;

function hideTotalFigures(stats: PortalEmploymentStatistics): void {
    stats.meanTotalSalary = hiddenBySetting;
    stats.meanTotalPayGap = hiddenBySetting;
    stats.medianTotalSalary = hiddenBySetting;
    stats.medianTotalPayGap = hiddenBySetting;
}

/**
 * Job profile subsections switched on in Involve settings but with no data behind them —
 * the "no data" state, which renders each subsection's empty state rather than nothing.
 */
function buildJobProfileEmpty(): EmploymentDashboardData {
    const data = buildAllVisible();

    if (data.jobProfile.visibility === 'visible') {
        data.jobProfile.data.payRange = visibleSection(null);
        data.jobProfile.data.description = visibleSection(null);
        data.jobProfile.data.averagePayLevelsForJob = visibleSection(null);
        data.jobProfile.data.averagePayLevelsForGrade = visibleSection(null);
    }

    return data;
}

/** Every section visible, with each paired section's total half switched off so only base salary remains. */
function buildTotalHidden(): EmploymentDashboardData {
    const data = buildAllVisible();

    if (data.jobProfile.visibility === 'visible') {
        const jobProfile = data.jobProfile.data;

        if (jobProfile.averagePayLevelsForJob.visibility === 'visible' && jobProfile.averagePayLevelsForJob.data != null) {
            jobProfile.averagePayLevelsForJob.data.visible.totalSalary = false;
            const stats = jobProfile.averagePayLevelsForJob.data.employeeJobProfileSalaryStatistics;
            stats.meanTotalSalary = hiddenBySetting;
            stats.meanTotalSalaryWomen = hiddenBySetting;
            stats.meanTotalSalaryMen = hiddenBySetting;
        }

        if (jobProfile.averagePayLevelsForGrade.visibility === 'visible' && jobProfile.averagePayLevelsForGrade.data != null) {
            jobProfile.averagePayLevelsForGrade.data.visible.totalSalary = false;
            const stats = jobProfile.averagePayLevelsForGrade.data.jobGradeStatistics;
            stats.totalPayGap = hiddenBySetting;
            stats.averageTotalSalary = hiddenBySetting;
        }
    }

    if (data.career.visibility === 'visible') {
        data.career.data.visible.totalSalary = false;
        for (const salary of data.career.data.historicalSalaries) {
            salary.totalSalary = hiddenBySetting;
        }
    }

    if (data.genderEquality.visibility === 'visible') {
        const genderEquality = data.genderEquality.data;

        if (genderEquality.payQuartiles.visibility === 'visible') {
            genderEquality.payQuartiles.data.visible.totalSalary = false;
            const quartile = genderEquality.payQuartiles.data.payQuartile;
            quartile.upper = hiddenBySetting;
            quartile.upperMiddle = hiddenBySetting;
            quartile.lowerMiddle = hiddenBySetting;
            quartile.lower = hiddenBySetting;
            hideTotalFigures(genderEquality.payQuartiles.data.employmentStatistics);
        }

        if (genderEquality.totalSalaryGaps.visibility === 'visible') {
            genderEquality.totalSalaryGaps.data.visible.totalSalary = false;
            hideTotalFigures(genderEquality.totalSalaryGaps.data.employmentStatistics);
        }

        if (genderEquality.variablePayGaps.visibility === 'visible') {
            genderEquality.variablePayGaps.data.visible.totalSalary = false;
            const { unadjustedMeanStatistics: mean, unadjustedMedianStatistics: median } = genderEquality.variablePayGaps.data;
            mean.femaleMeanTotalSalaryAvg = hiddenBySetting;
            mean.maleMeanTotalSalaryAvg = hiddenBySetting;
            mean.totalPayGap = hiddenBySetting;
            median.femaleMedianTotalSalaryAvg = hiddenBySetting;
            median.maleMedianTotalSalaryAvg = hiddenBySetting;
            median.totalPayGap = hiddenBySetting;
        }
    }

    return data;
}

function buildAllVisible(): EmploymentDashboardData {
    return {
        employment: visibleSection({
            name: 'Anna Svensson',
            position: 'Senior Developer',
            email: 'anna.svensson@example.com',
            profileImage: { name: 'Anna Svensson', gender: Gender.FEMALE },
        }),
        jobProfile: visibleSection({
            jobName: 'Software Engineer',
            jobLevel: 'L4',
            careerLevel: CareerType.IndividualContributor,
            payRange: visibleSection({
                min: 40000,
                max: 70000,
                meanSalary: 55000,
                isValid: true,
                salary: 52000,
                currency: 'SEK',
                selectedSalaryType: EmploymentSalaryType.monthly,
                jobLevel: 'L4',
                jobName: 'Software Engineer',
            }),
            description: visibleSection({ comment: 'Develops and maintains software systems.' }),
            averagePayLevelsForJob: visibleSection({
                visible: { baseSalary: true, totalSalary: true },
                employeeJobProfileSalaryStatistics: {
                    meanBaseSalary: { suppressed: false, value: 53000 },
                    meanTotalSalary: { suppressed: false, value: 58000 },
                    meanBaseSalaryWomen: { suppressed: false, value: 51000 },
                    meanTotalSalaryWomen: { suppressed: false, value: 56000 },
                    meanBaseSalaryMen: { suppressed: false, value: 55000 },
                    meanTotalSalaryMen: { suppressed: false, value: 60000 },
                },
                jobName: 'Software Engineer',
                currency: 'SEK',
                selectedSalaryType: EmploymentSalaryType.monthly,
            }),
            averagePayLevelsForGrade: visibleSection({
                visible: { baseSalary: true, totalSalary: true },
                jobGradeStatistics: {
                    basePayGap: { suppressed: false, value: 0.04 },
                    totalPayGap: { suppressed: false, value: 0.05 },
                    averageBaseSalary: { suppressed: false, value: { female: 51000, male: 55000 } },
                    averageTotalSalary: { suppressed: false, value: { female: 56000, male: 60000 } },
                    genderDistribution: { suppressed: false, value: { female: 12, male: 18 } },
                },
                jobLevel: 'L4',
                currency: 'SEK',
                selectedSalaryType: EmploymentSalaryType.monthly,
            }),
        }),
        totalSalary: visibleSection({
            salaryBreakdown: {
                salaryType: EmploymentSalaryType.monthly,
                hourlySalary: 0,
                salary: 52000,
                salaryIncrement: 0,
                secondSalaryIncrement: 0,
                thirdSalaryIncrement: 0,
                shortTermIncentive: 3000,
                longTermIncentive: 0,
                oneTimeBonus: 0,
                benifitsInKind: 0,
                overtime: 0,
                occupationalPension: 5000,
                paidHoliday: 0,
                carAllowance: 0,
            },
            currency: 'SEK',
            selectedSalaryType: EmploymentSalaryType.monthly,
        }),
        career: visibleSection({
            visible: { baseSalary: true, totalSalary: true },
            historicalSalaries: [
                { id: '1', currency: 'SEK', salaryYear: 2022, baseSalary: { suppressed: false, value: 48000 }, totalSalary: { suppressed: false, value: 51000 } },
                { id: '2', currency: 'SEK', salaryYear: 2023, baseSalary: { suppressed: false, value: 50000 }, totalSalary: { suppressed: false, value: 53000 } },
                { id: '3', currency: 'SEK', salaryYear: 2024, baseSalary: { suppressed: false, value: 52000 }, totalSalary: { suppressed: false, value: 55000 } },
            ],
            currency: 'SEK',
            selectedSalaryType: EmploymentSalaryType.monthly,
        }),
        genderEquality: visibleSection({
            payQuartiles: visibleSection({
                visible: { baseSalary: true, totalSalary: true },
                payQuartile: {
                    upper: { suppressed: false, value: { female: 6, male: 14, notDeclared: 0 } },
                    upperMiddle: { suppressed: false, value: { female: 8, male: 12, notDeclared: 0 } },
                    lowerMiddle: { suppressed: false, value: { female: 10, male: 10, notDeclared: 0 } },
                    lower: { suppressed: false, value: { female: 14, male: 6, notDeclared: 0 } },
                    baseSalaryUpper: { suppressed: false, value: { female: 7, male: 13, notDeclared: 0 } },
                    baseSalaryUpperMiddle: { suppressed: false, value: { female: 9, male: 11, notDeclared: 0 } },
                    baseSalaryLowerMiddle: { suppressed: false, value: { female: 11, male: 9, notDeclared: 0 } },
                    baseSalaryLower: { suppressed: false, value: { female: 13, male: 7, notDeclared: 0 } },
                },
                employmentStatistics: {
                    medianBasePayGap: { suppressed: false, value: 0.08 },
                    medianTotalPayGap: { suppressed: false, value: 0.09 },
                    medianBaseSalary: { suppressed: false, value: { female: 50000, male: 54000 } },
                    medianTotalSalary: { suppressed: false, value: { female: 55000, male: 60000 } },
                    genderDistribution: { suppressed: false, value: { female: 40, male: 60 } },
                    meanBasePayGap: { suppressed: false, value: 0.07 },
                    meanTotalPayGap: { suppressed: false, value: 0.08 },
                    meanBaseSalary: { suppressed: false, value: { female: 51000, male: 54000 } },
                    meanTotalSalary: { suppressed: false, value: { female: 56000, male: 60000 } },
                },
                currency: 'SEK',
                selectedSalaryType: EmploymentSalaryType.monthly,
            }),
            totalSalaryGaps: visibleSection({
                visible: { baseSalary: true, totalSalary: true },
                employmentStatistics: {
                    medianBasePayGap: { suppressed: false, value: 0.08 },
                    medianTotalPayGap: { suppressed: false, value: 0.09 },
                    medianBaseSalary: { suppressed: false, value: { female: 50000, male: 54000 } },
                    medianTotalSalary: { suppressed: false, value: { female: 55000, male: 60000 } },
                    genderDistribution: { suppressed: false, value: { female: 40, male: 60 } },
                    meanBasePayGap: { suppressed: false, value: 0.07 },
                    meanTotalPayGap: { suppressed: false, value: 0.08 },
                    meanBaseSalary: { suppressed: false, value: { female: 51000, male: 54000 } },
                    meanTotalSalary: { suppressed: false, value: { female: 56000, male: 60000 } },
                },
                currency: 'SEK',
                selectedSalaryType: EmploymentSalaryType.monthly,
            }),
            variablePayGaps: visibleSection({
                visible: { baseSalary: true, totalSalary: true },
                unadjustedMeanStatistics: {
                    femaleMeanBaseSalaryAvg: { suppressed: false, value: 51000 },
                    femaleMeanTotalSalaryAvg: { suppressed: false, value: 56000 },
                    maleMeanBaseSalaryAvg: { suppressed: false, value: 55000 },
                    maleMeanTotalSalaryAvg: { suppressed: false, value: 60000 },
                    basePayGap: { suppressed: false, value: 0.07 },
                    totalPayGap: { suppressed: false, value: 0.08 },
                    femaleMeanSalaryComponentsAvg: { suppressed: false, value: 3000 },
                    maleMeanSalaryComponentsAvg: { suppressed: false, value: 4000 },
                    payGapComponents: { suppressed: false, value: 0.25 },
                },
                unadjustedMedianStatistics: {
                    femaleMedianBaseSalaryAvg: { suppressed: false, value: 50000 },
                    femaleMedianTotalSalaryAvg: { suppressed: false, value: 55000 },
                    maleMedianBaseSalaryAvg: { suppressed: false, value: 54000 },
                    maleMedianTotalSalaryAvg: { suppressed: false, value: 59000 },
                    basePayGap: { suppressed: false, value: 0.08 },
                    totalPayGap: { suppressed: false, value: 0.09 },
                    femaleMedianSalaryComponentsAvg: { suppressed: false, value: 2500 },
                    maleMedianSalaryComponentsAvg: { suppressed: false, value: 3500 },
                    payGapComponents: { suppressed: false, value: 0.28 },
                },
                salaryComponentsPercentageStatistics: {
                    femalePercentage: { suppressed: false, value: 0.65 },
                    malePercentage: { suppressed: false, value: 0.75 },
                    salaryComponents: ['Short Term Incentive'],
                },
                currency: 'SEK',
                selectedSalaryType: EmploymentSalaryType.monthly,
            }),
        }),
        performance: visibleSection({
            reviews: [],
            isSelfAssessmentDisabled: false,
        }),
        targets: visibleSection({
            targets: [],
        }),
        statutoryGrade: hiddenBySettings(),
    };
}

const gdpr = { suppressed: true, reason: 'gdpr-threshold' } as const;

function buildAllSuppressed(): EmploymentDashboardData {
    return {
        employment: visibleSection({
            name: 'Anna Svensson',
            position: 'Senior Developer',
            email: 'anna.svensson@example.com',
            profileImage: { name: 'Anna Svensson', gender: Gender.FEMALE },
        }),
        jobProfile: visibleSection({
            jobName: 'Software Engineer',
            jobLevel: 'L4',
            careerLevel: CareerType.IndividualContributor,
            payRange: hiddenBySettings(),
            description: visibleSection({ comment: 'Develops and maintains software systems.' }),
            averagePayLevelsForJob: visibleSection({
                visible: { baseSalary: true, totalSalary: true },
                employeeJobProfileSalaryStatistics: {
                    meanBaseSalary: gdpr,
                    meanTotalSalary: gdpr,
                    meanBaseSalaryWomen: gdpr,
                    meanTotalSalaryWomen: gdpr,
                    meanBaseSalaryMen: gdpr,
                    meanTotalSalaryMen: gdpr,
                },
                jobName: 'Software Engineer',
                currency: 'SEK',
                selectedSalaryType: EmploymentSalaryType.monthly,
            }),
            averagePayLevelsForGrade: visibleSection({
                visible: { baseSalary: true, totalSalary: true },
                jobGradeStatistics: {
                    basePayGap: gdpr,
                    totalPayGap: gdpr,
                    averageBaseSalary: gdpr,
                    averageTotalSalary: gdpr,
                    genderDistribution: gdpr,
                },
                jobLevel: 'L4',
                currency: 'SEK',
                selectedSalaryType: EmploymentSalaryType.monthly,
            }),
        }),
        totalSalary: visibleSection({
            salaryBreakdown: {
                salaryType: EmploymentSalaryType.monthly,
                hourlySalary: 0,
                salary: 52000,
                salaryIncrement: 0,
                secondSalaryIncrement: 0,
                thirdSalaryIncrement: 0,
                shortTermIncentive: 3000,
                longTermIncentive: 0,
                oneTimeBonus: 0,
                benifitsInKind: 0,
                overtime: 0,
                occupationalPension: 5000,
                paidHoliday: 0,
                carAllowance: 0,
            },
            currency: 'SEK',
            selectedSalaryType: EmploymentSalaryType.monthly,
        }),
        career: visibleSection({
            visible: { baseSalary: true, totalSalary: true },
            historicalSalaries: [
                { id: '1', currency: 'SEK', salaryYear: 2022, baseSalary: { suppressed: false, value: 48000 }, totalSalary: { suppressed: false, value: 51000 } },
                { id: '2', currency: 'SEK', salaryYear: 2023, baseSalary: { suppressed: false, value: 50000 }, totalSalary: { suppressed: false, value: 53000 } },
                { id: '3', currency: 'SEK', salaryYear: 2024, baseSalary: { suppressed: false, value: 52000 }, totalSalary: { suppressed: false, value: 55000 } },
            ],
            currency: 'SEK',
            selectedSalaryType: EmploymentSalaryType.monthly,
        }),
        genderEquality: visibleSection({
            payQuartiles: visibleSection({
                visible: { baseSalary: true, totalSalary: true },
                payQuartile: {
                    upper: gdpr,
                    upperMiddle: gdpr,
                    lowerMiddle: gdpr,
                    lower: gdpr,
                    baseSalaryUpper: gdpr,
                    baseSalaryUpperMiddle: gdpr,
                    baseSalaryLowerMiddle: gdpr,
                    baseSalaryLower: gdpr,
                },
                employmentStatistics: {
                    medianBasePayGap: gdpr,
                    medianTotalPayGap: gdpr,
                    medianBaseSalary: gdpr,
                    medianTotalSalary: gdpr,
                    genderDistribution: gdpr,
                    meanBasePayGap: gdpr,
                    meanTotalPayGap: gdpr,
                    meanBaseSalary: gdpr,
                    meanTotalSalary: gdpr,
                },
                currency: 'SEK',
                selectedSalaryType: EmploymentSalaryType.monthly,
            }),
            totalSalaryGaps: visibleSection({
                visible: { baseSalary: true, totalSalary: true },
                employmentStatistics: {
                    medianBasePayGap: gdpr,
                    medianTotalPayGap: gdpr,
                    medianBaseSalary: gdpr,
                    medianTotalSalary: gdpr,
                    genderDistribution: gdpr,
                    meanBasePayGap: gdpr,
                    meanTotalPayGap: gdpr,
                    meanBaseSalary: gdpr,
                    meanTotalSalary: gdpr,
                },
                currency: 'SEK',
                selectedSalaryType: EmploymentSalaryType.monthly,
            }),
            variablePayGaps: visibleSection({
                visible: { baseSalary: true, totalSalary: true },
                unadjustedMeanStatistics: {
                    femaleMeanBaseSalaryAvg: gdpr,
                    femaleMeanTotalSalaryAvg: gdpr,
                    maleMeanBaseSalaryAvg: gdpr,
                    maleMeanTotalSalaryAvg: gdpr,
                    basePayGap: gdpr,
                    totalPayGap: gdpr,
                    femaleMeanSalaryComponentsAvg: gdpr,
                    maleMeanSalaryComponentsAvg: gdpr,
                    payGapComponents: gdpr,
                },
                unadjustedMedianStatistics: {
                    femaleMedianBaseSalaryAvg: gdpr,
                    femaleMedianTotalSalaryAvg: gdpr,
                    maleMedianBaseSalaryAvg: gdpr,
                    maleMedianTotalSalaryAvg: gdpr,
                    basePayGap: gdpr,
                    totalPayGap: gdpr,
                    femaleMedianSalaryComponentsAvg: gdpr,
                    maleMedianSalaryComponentsAvg: gdpr,
                    payGapComponents: gdpr,
                },
                salaryComponentsPercentageStatistics: {
                    femalePercentage: gdpr,
                    malePercentage: gdpr,
                    salaryComponents: [],
                },
                currency: 'SEK',
                selectedSalaryType: EmploymentSalaryType.monthly,
            }),
        }),
        performance: visibleSection({
            reviews: [],
            isSelfAssessmentDisabled: false,
        }),
        targets: visibleSection({
            targets: [],
        }),
        statutoryGrade: hiddenBySettings(),
    };
}

function buildMixed(): EmploymentDashboardData {
    return {
        employment: visibleSection({
            name: 'Anna Svensson',
            position: 'Senior Developer',
            email: 'anna.svensson@example.com',
            profileImage: { name: 'Anna Svensson', gender: Gender.FEMALE },
        }),
        jobProfile: visibleSection({
            jobName: 'Software Engineer',
            jobLevel: 'L4',
            careerLevel: CareerType.IndividualContributor,
            payRange: visibleSection({
                min: 40000,
                max: 70000,
                meanSalary: 55000,
                isValid: true,
                salary: 52000,
                currency: 'SEK',
                selectedSalaryType: EmploymentSalaryType.monthly,
                jobLevel: 'L4',
                jobName: 'Software Engineer',
            }),
            description: visibleSection({ comment: 'Develops and maintains software systems.' }),
            averagePayLevelsForJob: visibleSection({
                visible: { baseSalary: true, totalSalary: true },
                employeeJobProfileSalaryStatistics: {
                    meanBaseSalary: { suppressed: false, value: 53000 },
                    meanTotalSalary: { suppressed: false, value: 58000 },
                    meanBaseSalaryWomen: gdpr,
                    meanTotalSalaryWomen: gdpr,
                    meanBaseSalaryMen: gdpr,
                    meanTotalSalaryMen: gdpr,
                },
                jobName: 'Software Engineer',
                currency: 'SEK',
                selectedSalaryType: EmploymentSalaryType.monthly,
            }),
            averagePayLevelsForGrade: visibleSection({
                visible: { baseSalary: true, totalSalary: true },
                jobGradeStatistics: {
                    basePayGap: { suppressed: false, value: 0.04 },
                    totalPayGap: { suppressed: false, value: 0.05 },
                    averageBaseSalary: { suppressed: false, value: { female: 51000, male: 55000 } },
                    averageTotalSalary: { suppressed: false, value: { female: 56000, male: 60000 } },
                    genderDistribution: { suppressed: false, value: { female: 12, male: 18 } },
                },
                jobLevel: 'L4',
                currency: 'SEK',
                selectedSalaryType: EmploymentSalaryType.monthly,
            }),
        }),
        totalSalary: visibleSection({
            salaryBreakdown: {
                salaryType: EmploymentSalaryType.monthly,
                hourlySalary: 0,
                salary: 52000,
                salaryIncrement: 0,
                secondSalaryIncrement: 0,
                thirdSalaryIncrement: 0,
                shortTermIncentive: 3000,
                longTermIncentive: 0,
                oneTimeBonus: 0,
                benifitsInKind: 0,
                overtime: 0,
                occupationalPension: 5000,
                paidHoliday: 0,
                carAllowance: 0,
            },
            currency: 'SEK',
            selectedSalaryType: EmploymentSalaryType.monthly,
        }),
        career: visibleSection({
            visible: { baseSalary: true, totalSalary: true },
            historicalSalaries: [
                { id: '1', currency: 'SEK', salaryYear: 2022, baseSalary: { suppressed: false, value: 48000 }, totalSalary: { suppressed: false, value: 51000 } },
                { id: '2', currency: 'SEK', salaryYear: 2023, baseSalary: { suppressed: false, value: 50000 }, totalSalary: { suppressed: false, value: 53000 } },
                { id: '3', currency: 'SEK', salaryYear: 2024, baseSalary: { suppressed: false, value: 52000 }, totalSalary: { suppressed: false, value: 55000 } },
            ],
            currency: 'SEK',
            selectedSalaryType: EmploymentSalaryType.monthly,
        }),
        genderEquality: visibleSection({
            payQuartiles: visibleSection({
                visible: { baseSalary: true, totalSalary: true },
                payQuartile: {
                    upper: gdpr,
                    upperMiddle: gdpr,
                    lowerMiddle: { suppressed: false, value: { female: 10, male: 10, notDeclared: 0 } },
                    lower: { suppressed: false, value: { female: 14, male: 6, notDeclared: 0 } },
                    baseSalaryUpper: gdpr,
                    baseSalaryUpperMiddle: gdpr,
                    baseSalaryLowerMiddle: { suppressed: false, value: { female: 10, male: 10, notDeclared: 0 } },
                    baseSalaryLower: { suppressed: false, value: { female: 14, male: 6, notDeclared: 0 } },
                },
                employmentStatistics: {
                    medianBasePayGap: gdpr,
                    medianTotalPayGap: gdpr,
                    medianBaseSalary: gdpr,
                    medianTotalSalary: gdpr,
                    genderDistribution: { suppressed: false, value: { female: 40, male: 60 } },
                    meanBasePayGap: gdpr,
                    meanTotalPayGap: gdpr,
                    meanBaseSalary: gdpr,
                    meanTotalSalary: gdpr,
                },
                currency: 'SEK',
                selectedSalaryType: EmploymentSalaryType.monthly,
            }),
            totalSalaryGaps: visibleSection({
                visible: { baseSalary: true, totalSalary: true },
                employmentStatistics: {
                    medianBasePayGap: { suppressed: false, value: 0.08 },
                    medianTotalPayGap: { suppressed: false, value: 0.09 },
                    medianBaseSalary: { suppressed: false, value: { female: 50000, male: 54000 } },
                    medianTotalSalary: { suppressed: false, value: { female: 55000, male: 60000 } },
                    genderDistribution: { suppressed: false, value: { female: 40, male: 60 } },
                    meanBasePayGap: { suppressed: false, value: 0.07 },
                    meanTotalPayGap: { suppressed: false, value: 0.08 },
                    meanBaseSalary: { suppressed: false, value: { female: 51000, male: 54000 } },
                    meanTotalSalary: { suppressed: false, value: { female: 56000, male: 60000 } },
                },
                currency: 'SEK',
                selectedSalaryType: EmploymentSalaryType.monthly,
            }),
            variablePayGaps: visibleSection({
                visible: { baseSalary: true, totalSalary: true },
                unadjustedMeanStatistics: {
                    femaleMeanBaseSalaryAvg: gdpr,
                    femaleMeanTotalSalaryAvg: gdpr,
                    maleMeanBaseSalaryAvg: gdpr,
                    maleMeanTotalSalaryAvg: gdpr,
                    basePayGap: gdpr,
                    totalPayGap: gdpr,
                    femaleMeanSalaryComponentsAvg: gdpr,
                    maleMeanSalaryComponentsAvg: gdpr,
                    payGapComponents: gdpr,
                },
                unadjustedMedianStatistics: {
                    femaleMedianBaseSalaryAvg: gdpr,
                    femaleMedianTotalSalaryAvg: gdpr,
                    maleMedianBaseSalaryAvg: gdpr,
                    maleMedianTotalSalaryAvg: gdpr,
                    basePayGap: gdpr,
                    totalPayGap: gdpr,
                    femaleMedianSalaryComponentsAvg: gdpr,
                    maleMedianSalaryComponentsAvg: gdpr,
                    payGapComponents: gdpr,
                },
                salaryComponentsPercentageStatistics: {
                    femalePercentage: gdpr,
                    malePercentage: gdpr,
                    salaryComponents: [],
                },
                currency: 'SEK',
                selectedSalaryType: EmploymentSalaryType.monthly,
            }),
        }),
        performance: visibleSection({
            reviews: [],
            isSelfAssessmentDisabled: false,
        }),
        targets: visibleSection({
            targets: [],
        }),
        statutoryGrade: hiddenBySettings(),
    };
}
